import type { TFunction } from 'i18next';
import type {
  IeltsBandReport,
  IeltsScope,
  IeltsScopeSkill,
  IeltsWritingTask,
  SectionKind,
  SpeakingAnswer,
  SpeakingPart,
  SpeakingRecording,
} from './types';

/** i18n keys for section kinds. */
export const KIND_LABEL_KEY: Record<SectionKind, string> = {
  GENERIC: 'exam.kind.generic',
  IELTS_LISTENING: 'exam.kind.listening',
  IELTS_READING: 'exam.kind.reading',
  IELTS_WRITING: 'exam.kind.writing',
  IELTS_SPEAKING: 'exam.kind.speaking',
  SAT_RW: 'exam.kind.sat_rw',
  SAT_MATH: 'exam.kind.sat_math',
};

export const LIBRARY_KINDS: SectionKind[] = ['IELTS_LISTENING', 'IELTS_READING', 'IELTS_WRITING', 'IELTS_SPEAKING', 'SAT_RW', 'SAT_MATH'];

/** Mirrors IELTS_PART_COUNT in the API's exam-blueprints.ts: a Listening part, Reading passage, Writing task or Speaking part. */
export const IELTS_PART_COUNT: Record<IeltsScopeSkill, number> = { LISTENING: 4, READING: 3, WRITING: 2, SPEAKING: 3 };

export const IELTS_SCOPE_SKILLS: IeltsScopeSkill[] = ['LISTENING', 'READING', 'WRITING', 'SPEAKING'];

export const SCOPE_SKILL_KEY: Record<IeltsScopeSkill, string> = {
  LISTENING: 'exam.kind.listening',
  READING: 'exam.kind.reading',
  WRITING: 'exam.kind.writing',
  SPEAKING: 'exam.kind.speaking',
};

/** "Part {{n}}", "Passage {{n}}" or "Task {{n}}", by skill. */
export const SCOPE_PART_KEY: Record<IeltsScopeSkill, string> = {
  LISTENING: 'exam.scope.part_n',
  READING: 'exam.scope.passage_n',
  WRITING: 'exam.scope.task_n',
  SPEAKING: 'exam.scope.part_n',
};

export type IeltsScopeMode = 'FULL' | 'SKILL' | 'PART';

/** Whether a scope narrows a skill to one part. Reads `null` like `undefined`, as a part left unset can be stored as null. */
export const isPartScoped = (scope: IeltsScope | null | undefined): scope is IeltsScope & { part: number } => scope?.part != null;

/** No scope is the full test; a scope without a part is one skill; with a part, one part of one skill. */
export const scopeMode = (scope: IeltsScope | undefined): IeltsScopeMode =>
  !scope ? 'FULL' : isPartScoped(scope) ? 'PART' : 'SKILL';

/** "Listening" or "Listening · Part 2". */
export function scopeLabel(t: TFunction, scope: IeltsScope): string {
  const skill = t(SCOPE_SKILL_KEY[scope.skill]);
  return isPartScoped(scope) ? `${skill} · ${t(SCOPE_PART_KEY[scope.skill], { n: scope.part })}` : skill;
}

/** A Writing part is one task, so its question cannot be the other one. */
export function fixedWritingTask(scope: IeltsScope | undefined): IeltsWritingTask | undefined {
  if (scope?.skill !== 'WRITING' || !isPartScoped(scope)) return undefined;
  return scope.part === 1 ? 'TASK_1' : 'TASK_2';
}

export type IeltsHeadline =
  | { kind: 'OVERALL'; band: number | null }
  | { kind: 'SKILL'; scope: IeltsScope; band: number | null }
  | { kind: 'RAW'; scope: IeltsScope; raw: number; max: number; percent: number; band: number | null };

/**
 * What the report leads with: the overall band of a full test, the skill band
 * of a Writing/Speaking test, or the raw score of any Listening/Reading test.
 * A whole 40-question paper also carries its band (the chip beside the raw
 * score); a part is too short for the band tables, so the API sends none.
 */
export function ieltsHeadline(report: IeltsBandReport): IeltsHeadline {
  const { scope } = report;
  if (!scope) return { kind: 'OVERALL', band: report.overall };
  if (scope.skill === 'LISTENING' || scope.skill === 'READING') {
    const score = scope.skill === 'LISTENING' ? report.listening : report.reading;
    const raw = score?.raw ?? 0;
    const max = score?.max ?? 0;
    return { kind: 'RAW', scope, raw, max, percent: max > 0 ? Math.round((raw / max) * 100) : 0, band: report.overall ?? score?.band ?? null };
  }
  return { kind: 'SKILL', scope, band: report.overall };
}

export const isSatKind = (kind?: string): kind is 'SAT_RW' | 'SAT_MATH' => kind === 'SAT_RW' || kind === 'SAT_MATH';

/** Mirrors WRITING_CRITERIA in the API's ielts-scoring.ts. */
export const WRITING_CRITERIA: Record<IeltsWritingTask, string[]> = {
  TASK_1: ['TA', 'CC', 'LR', 'GRA'],
  TASK_2: ['TR', 'CC', 'LR', 'GRA'],
};

/** Mirrors SPEAKING_CRITERIA in the API's ielts-scoring.ts. */
export const SPEAKING_CRITERIA = ['FC', 'LR', 'GRA', 'P'];

export const CRITERION_LABEL_KEY: Record<string, string> = {
  FC: 'exam.criterion.fc',
  P: 'exam.criterion.p',
  TA: 'exam.criterion.ta',
  TR: 'exam.criterion.tr',
  CC: 'exam.criterion.cc',
  LR: 'exam.criterion.lr',
  GRA: 'exam.criterion.gra',
};

export const MIN_WORDS: Record<IeltsWritingTask, number> = { TASK_1: 150, TASK_2: 250 };

export const BANDS: number[] = Array.from({ length: 19 }, (_, i) => i / 2); // 0, 0.5 … 9

export function countWords(text: string): number {
  const plain = text.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  return plain ? plain.split(/\s+/).length : 0;
}

/** Candidate-side SPR input limits (the server applies the full rules when grading). */
export const SPR_MAX_CHARS = 5;
export const SPR_MAX_CHARS_NEGATIVE = 6;

/** Keeps only characters the grid accepts and enforces the length limit. */
export function sanitizeSprInput(raw: string): string {
  const cleaned = raw.replace(/[^0-9./-]/g, '');
  const negative = cleaned.startsWith('-');
  const body = cleaned.replace(/-/g, '');
  const value = (negative ? '-' : '') + body;
  return value.slice(0, negative ? SPR_MAX_CHARS_NEGATIVE : SPR_MAX_CHARS);
}

/** Why a typed grid-in answer is not a valid entry, or null. Mirrors apps/api/src/common/spr.ts. */
export function sprInputIssue(value: string): 'EMPTY' | 'TOO_LONG' | 'INVALID' | 'ZERO_DENOMINATOR' | null {
  const v = value.trim();
  if (!v) return 'EMPTY';
  if (v.length > (v.startsWith('-') ? SPR_MAX_CHARS_NEGATIVE : SPR_MAX_CHARS)) return 'TOO_LONG';
  if (!/^-?(?:\d+\/\d+|\d+(?:\.\d*)?|\.\d+)$/.test(v)) return 'INVALID';
  if (v.includes('/') && Number(v.split('/')[1]) === 0) return 'ZERO_DENOMINATOR';
  return null;
}

/** Time left in whole seconds against a server deadline, corrected for clock skew. */
export function secondsLeft(deadlineAt: string | null | undefined, skewMs: number, now = Date.now()): number | null {
  if (!deadlineAt) return null;
  return Math.max(0, Math.ceil((new Date(deadlineAt).getTime() - (now + skewMs)) / 1000));
}

export function formatClock(total: number): string {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

export const cefrLabel = (band: number | null | undefined): string | null => {
  if (band === null || band === undefined) return null;
  if (band >= 8.5) return 'C2';
  if (band >= 7) return 'C1';
  if (band >= 5.5) return 'B2';
  if (band >= 4) return 'B1';
  return 'A2';
};

// ─── SPR answer tester (author preview; mirrors apps/api/src/common/spr.ts) ────

const SPR_PATTERN = /^-?(?:\d+\/\d+|\d+(?:\.\d*)?|\.\d+)$/;

export function parseSprNumber(input: string): number | null {
  const value = input.trim().replace(/\s+/g, '');
  if (!value || !SPR_PATTERN.test(value)) return null;
  if (value.includes('/')) {
    const negative = value.startsWith('-');
    const [num, den] = value.replace('-', '').split('/').map(Number);
    if (!den) return null;
    return (negative ? -num : num) / den;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const same = (a: number, b: number) => Math.abs(a - b) < 1e-9;

/** Would the real grader accept `answer` for these keys? */
export function isSprAnswerCorrect(answer: string, keys: string[]): boolean {
  if (sprInputIssue(answer) !== null) return false;
  const value = answer.trim();
  const parsed = parseSprNumber(value);
  if (parsed === null) return false;
  for (const key of keys) {
    const target = parseSprNumber(key);
    if (target === null) continue;
    if (same(parsed, target)) return true;
    if (value.includes('/') || !value.includes('.')) continue;
    const limit = value.startsWith('-') ? SPR_MAX_CHARS_NEGATIVE : SPR_MAX_CHARS;
    if (value.length !== limit) continue;
    const decimals = value.length - value.indexOf('.') - 1;
    const factor = 10 ** decimals;
    if (same(parsed, Math.trunc(target * factor) / factor) || same(parsed, Math.round(target * factor) / factor)) return true;
  }
  return false;
}

/** Content domains used by the SAT domain breakdown. */
export const SAT_DOMAINS: Record<'SAT_RW' | 'SAT_MATH', string[]> = {
  SAT_RW: ['Information and Ideas', 'Craft and Structure', 'Expression of Ideas', 'Standard English Conventions'],
  SAT_MATH: ['Algebra', 'Advanced Math', 'Problem-Solving and Data Analysis', 'Geometry and Trigonometry'],
};

/** Question types each section kind offers in the editor. */
export const TYPES_FOR_KIND: Record<string, string[]> = {
  IELTS_LISTENING: ['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'SHORT_ANSWER', 'FILL_IN_BLANKS', 'MATCHING', 'TRUE_FALSE'],
  IELTS_READING: ['PASSAGE', 'MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE', 'SHORT_ANSWER', 'FILL_IN_BLANKS', 'MATCHING'],
  IELTS_WRITING: ['OPEN_ENDED'],
  IELTS_SPEAKING: ['SPEAKING'],
  SAT_RW: ['MULTIPLE_CHOICE'],
  SAT_MATH: ['MULTIPLE_CHOICE', 'SPR'],
};

/** Whether a candidate answer counts as given (text, choices, blanks or passage maps). */
export function isAnswered(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  if (value != null && typeof value === 'object') return Object.keys(value as object).length > 0;
  return false;
}

/** Questions per IELTS Listening part on the real paper. */
export const LISTENING_PART_SIZE = 10;
/** Parts on the real Listening paper; more recordings than this are clips within parts. */
export const LISTENING_PARTS = 4;

/**
 * The group each audio clip belongs to, in order: a `breakAfter` divider on a
 * clip starts a new group after it. `[intro, S1, S1-end │ S2]` -> [0, 0, 0, 1].
 * A divider on the last clip separates nothing and is ignored.
 */
export function trackGroups(tracks: Array<{ breakAfter?: boolean }>): number[] {
  let group = 0;
  return tracks.map((track, i) => {
    const current = group;
    if (track.breakAfter && i < tracks.length - 1) group += 1;
    return current;
  });
}

/**
 * Splits Listening questions into parts. The teacher's dividers win: as many
 * parts as clip groups, split evenly (the last part takes the remainder).
 * Otherwise "questions per part" wins; otherwise one part per recording when
 * there are two to four, else groups of 10 as on the real paper (4 × 10). A
 * set split into many clips (narrations plus recordings) is not one part per
 * clip.
 */
export function listeningParts<T>(questions: T[], trackCount: number, questionsPerPart?: number, groupCount?: number): T[][] {
  if (!questions.length) return [[]];
  const grouped = (groupCount ?? 0) >= 2;
  const parts = grouped
    ? Math.min(groupCount as number, questions.length)
    : questionsPerPart
      ? Math.ceil(questions.length / questionsPerPart)
      : trackCount >= 2 && trackCount <= LISTENING_PARTS
        ? Math.min(trackCount, questions.length)
        : Math.ceil(questions.length / LISTENING_PART_SIZE);
  const size = (!grouped && questionsPerPart) || Math.ceil(questions.length / parts);
  return Array.from({ length: parts }, (_, i) =>
    questions.slice(i * size, i === parts - 1 ? undefined : (i + 1) * size),
  ).filter((p) => p.length);
}

/** One recordable Speaking prompt, in test order. */
export interface SpeakingPrompt {
  /** Answer key the server stores the recording under: `<partIndex>-<promptIndex>`. */
  key: string;
  part: 1 | 2 | 3;
  text: string;
  /** Part 2 cue card: preparation before the long turn. */
  prepSeconds: number;
  answerSeconds: number;
  /** The Part 2 cue card itself (shown as a card, with notes). */
  cueCard: boolean;
  /** A question the live examiner asked about the previous answer; its key ends in `-f1`. */
  followUp?: boolean;
}

/** Part 2 follow-up questions are short, as in the real test. */
export const SPEAKING_FOLLOW_UP_SECONDS = 30;

/** Flattens the parts into the order the candidate answers them. */
export function speakingPrompts(parts: SpeakingPart[] | undefined): SpeakingPrompt[] {
  return (parts ?? []).flatMap((part, p) =>
    part.prompts.map((text, i) => {
      const cueCard = part.part === 2 && i === 0;
      return {
        key: `${p}-${i}`,
        part: part.part,
        text,
        prepSeconds: cueCard ? part.prepSeconds ?? 60 : 0,
        answerSeconds: part.part === 2 && i > 0 ? Math.min(part.answerSeconds, SPEAKING_FOLLOW_UP_SECONDS) : part.answerSeconds,
        cueCard,
      };
    }),
  );
}

/**
 * A stored Speaking entry that has a recording. A follow-up the examiner asked
 * but the candidate has not answered yet is stored with its question only.
 */
export function isSpokenAnswer(entry: SpeakingRecording | undefined): boolean {
  return typeof entry?.audioKey === 'string';
}

/** Only scripted Part 1 and Part 3 prompts get a live follow-up (Part 2 has its own rounding-off questions). */
export function canFollowUp(prompt: SpeakingPrompt): boolean {
  return !prompt.followUp && (prompt.part === 1 || prompt.part === 3);
}

/** The scripted prompts with each follow-up the examiner has asked placed right after its prompt. */
export function withFollowUps(prompts: SpeakingPrompt[], recorded: SpeakingAnswer): SpeakingPrompt[] {
  return prompts.flatMap((prompt) => {
    const asked = canFollowUp(prompt) ? recorded[`${prompt.key}-f1`] : undefined;
    if (typeof asked?.prompt !== 'string') return [prompt];
    return [
      prompt,
      { key: `${prompt.key}-f1`, part: prompt.part, text: asked.prompt, prepSeconds: 0, answerSeconds: prompt.answerSeconds, cueCard: false, followUp: true },
    ];
  });
}
